import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  WritableSignal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { DecimalPipe, NgStyle } from "@angular/common";
import { createTimeoutSignal } from "../../utils/timeout.utils";
import { TimeoutComponent } from "../../components/timeout/timeout.component";
import { ForecastEnergyLoss, FutureTimeWindow, WeatherForecastDisplay } from "../../../models/ui/weatherForecast";
import { ForecastData } from "../../../models/api/telemetry/forecastData";
import { getWeatherIconForWmoCode } from "../../../constants/weatherConditions";
import { ASSET_TYPES, AssetType } from "../../../enums/assetTypes.enum";
import { isNotNullOrUndefined } from "../../utils/utils";
import { TooltipModule } from "@openng/optimus-ui/tooltip";
import { SvgIconComponent } from "../../components/svg-icon/svg-icon.component";
import { ForecastTelemetryEntry } from "../../../models/shared/telemetry";
import { CommonUtilsService } from "../../../services/utils/common-utils/common-utils.service";
const SVG_BASE_URL = "assets/icons/forecast_icons/";

@Component({
  standalone: true,
  selector: "app-weather-report",
  imports: [
    TranslateModule,
    ProgressSpinnerModule,
    NgStyle,
    TimeoutComponent,
    DecimalPipe,
    TooltipModule,
    SvgIconComponent,
  ],
  templateUrl: "./weather-report.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./weather-report.component.scss",
})
export class WeatherReportComponent {
  public forecastWeatherData = input<ForecastTelemetryEntry | null>();
  public forecastEnergyLossData = input<ForecastEnergyLoss | null>();
  public sourceType = input<AssetType | null>(null);

  public weatherData: WeatherForecastDisplay | null = null;
  public assetTypes = ASSET_TYPES;

  public displayedForecastEnergyLoss: WritableSignal<number | null> = signal(null);
  public expectedEnergyLossOptions: FutureTimeWindow[] = ["today", "tomorrow", "day_after_tomorrow"];

  public translate = inject(TranslateService);

  public loading = computed(() => this.forecastWeatherData() === null);
  public timeOut = createTimeoutSignal(this.loading);

  public dataIsCurrent = signal(true);

  private commonUtils = inject(CommonUtilsService);

  constructor() {
    effect(() => {
      const foreCastData = this.forecastWeatherData();
      const foreCastEnergyLoss = this.forecastEnergyLossData();
      if (foreCastEnergyLoss) {
        this.displayedForecastEnergyLoss.set(foreCastEnergyLoss.today);
      } else {
        this.displayedForecastEnergyLoss.set(null);
      }
      if (foreCastData) {
        const dataIsCurrent = this.commonUtils.isTimestampFromToday(foreCastData.timestamp);
        if (dataIsCurrent) {
          this.dataIsCurrent.set(true);
          this.setDataOnDisplay(foreCastData.value);
        } else {
          this.dataIsCurrent.set(false);
        }
      } else {
        this.weatherData = null;
      }
    });
  }

  /**
   * @returns height of the image in the weather report
   *  based on the height of the container,
   *  scaled down to fit the available space.
   */
  public getImageHeight() {
    const rect = document.getElementsByClassName("weatherReport")[0]?.getBoundingClientRect();

    return { height: rect?.height * 0.3 + "px" };
  }

  /**
   * Set the weather data for the display, given forecast data.
   * @param {ForecastData} forecastData - the forecast data to display
   * @returns {void}
   */
  private setDataOnDisplay(forecastData: ForecastData) {
    if (this.checkIfData(forecastData)) {
      this.weatherData = {
        today: {
          morning: {
            temperature: forecastData.today.timeOfDay[1].temp_c,
            icon: this.buildWeatherIcon(forecastData.today.timeOfDay[1].condition_code, false, "icon"),
            alt: this.buildWeatherIcon(forecastData.today.timeOfDay[1].condition_code, false, "alt"),
          },
          noon: {
            temperature: forecastData.today.timeOfDay[2].temp_c,
            icon: this.buildWeatherIcon(forecastData.today.timeOfDay[2].condition_code, false, "icon"),
            alt: this.buildWeatherIcon(forecastData.today.timeOfDay[2].condition_code, false, "alt"),
          },
          evening: {
            temperature: forecastData.today.timeOfDay[3].temp_c,
            icon: this.buildWeatherIcon(forecastData.today.timeOfDay[3].condition_code, true, "icon"),
            alt: this.buildWeatherIcon(forecastData.today.timeOfDay[3].condition_code, true, "alt"),
          },
        },
        tomorrow: {
          temperatureMin: forecastData.tomorrow.mintemp_c,
          temperatureMax: forecastData.tomorrow.maxtemp_c,
          icon: this.buildWeatherIcon(forecastData.tomorrow.condition_code, false, "icon"),
          alt: this.buildWeatherIcon(forecastData.tomorrow.condition_code, false, "alt"),
        },
        dayAfter: {
          temperatureMin: forecastData.day_after.mintemp_c,
          temperatureMax: forecastData.day_after.maxtemp_c,
          icon: this.buildWeatherIcon(forecastData.day_after.condition_code, false, "icon"),
          alt: this.buildWeatherIcon(forecastData.day_after.condition_code, false, "alt"),
        },
      };
    }
  }

  /**
   * Handles change events for the expected time window selection.
   * Updates the displayed forecast energy loss based on the selected time window (today, tomorrow, or day after tomorrow).
   * @param {Event} changeEvent - The change event from the time window selector
   */
  public onExpectedChange(changeEvent: Event) {
    const timeWindow = (changeEvent.target as any).value;
    let timeWindowEnergyLoss;
    switch (timeWindow) {
      case "tomorrow":
        timeWindowEnergyLoss = this.forecastEnergyLossData()?.tomorrow;
        break;
      case "today":
        timeWindowEnergyLoss = this.forecastEnergyLossData()?.today;
        break;
      case "day_after_tomorrow":
        timeWindowEnergyLoss = this.forecastEnergyLossData()?.dayAfterTomorrow;
        break;
      default:
        timeWindowEnergyLoss = null;
        break;
    }
    if (isNotNullOrUndefined(timeWindowEnergyLoss)) {
      this.displayedForecastEnergyLoss.set(timeWindowEnergyLoss);
    }
  }

  /**
   * Checks if the given data contains valid forecast data.
   * @param data the forecast data to check
   * @returns true if the data contains valid forecast data, false otherwise
   */
  private checkIfData(data: ForecastData): boolean {
    const slot = data.today?.timeOfDay?.[1];
    return isNotNullOrUndefined(slot) && typeof slot.temp_c === "number";
  }

  /**
   * Builds the url for the weather icon given the condition code and the flag for evening.
   * If the flag is set to true, it will return the day icon if the current month is between
   * April and October, otherwise it will return the night icon.
   * @param conditionCode the condition code for which the icon is requested
   * @param eveningFlag the flag to indicate if it is evening
   * @param iconOrAlt the flag to indicate if the icon or the alt text is requested
   * @returns the url of the requested icon or alt text
   */
  private buildWeatherIcon(conditionCode: number | null | undefined, eveningFlag: boolean, iconOrAlt: "icon" | "alt") {
    const now = new Date();
    const monthOfYear = now.getMonth();
    const timeOfDayKey = eveningFlag && (monthOfYear <= 2 || monthOfYear >= 10) ? "night" : "day";
    const mapping = getWeatherIconForWmoCode(conditionCode);

    if (iconOrAlt === "icon") {
      return SVG_BASE_URL + mapping[timeOfDayKey].icon + ".svg";
    }
    return mapping[timeOfDayKey].alt;
  }
}
